import { useState, useMemo, useRef, type RefObject } from 'react';
import { type ScheduleItem, type ScheduleLink, type ScheduleLinkType } from '@/lib/scheduleUtils';
import { validateScheduleLinkMutation } from '@/lib/scheduleDependencyEngine';

type LinkFormState = {
  otherItemId: string;
  link_type: ScheduleLinkType;
  lag_days: number;
};

interface Props {
  editItem: ScheduleItem | null;
  links: ScheduleLink[];
  scheduleItems: ScheduleItem[];
  canEdit: boolean;
  savingLink: boolean;
  deletingLinkId: string | null;
  onUpsertLink: (oldLinkId: string | null, payload: { predecessor_item_id: string; successor_item_id: string; link_type: ScheduleLinkType; lag_days: number }) => Promise<void>;
  onRemoveLink: (linkId: string) => Promise<void>;
  onRecalcLinkedItems: () => Promise<void>;
}

const LINK_LABELS: Record<string, { label: string; desc: string }> = {
  FS: { label: 'Finish to Start', desc: 'The next item starts after this item finishes.' },
  SS: { label: 'Start to Start', desc: 'The next item starts at the same time as this item.' },
  FF: { label: 'Finish to Finish', desc: 'Deprecated — not currently editable.' },
  SF: { label: 'Start to Finish', desc: 'Deprecated — not currently editable.' },
};
const ACTIVE_TYPES = ['FS', 'SS'] as const;
const ALL_TYPES = ['FS', 'SS', 'FF', 'SF'] as const;

export default function ScheduleItemModalLinksSection({
  editItem,
  links,
  scheduleItems,
  canEdit,
  savingLink,
  deletingLinkId,
  onUpsertLink,
  onRemoveLink,
  onRecalcLinkedItems,
}: Props) {
  const [addingPred, setAddingPred] = useState(false);
  const [addingSucc, setAddingSucc] = useState(false);
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [linkForm, setLinkForm] = useState<LinkFormState>({ otherItemId: '', link_type: 'FS', lag_days: 0 });
  const [linkSearch, setLinkSearch] = useState('');
  const [showCompletedOptions, setShowCompletedOptions] = useState(false);
  const [localError, setLocalError] = useState('');
  const [linkSaveSuccess, setLinkSaveSuccess] = useState('');
  const editFormAnchorRef = useRef<HTMLDivElement>(null);

  const clearLinkSuccess = () => { setLinkSaveSuccess(''); };

  const predecessors = useMemo(
    () =>
      links
        .filter((l) => l.successor_item_id === editItem?.id)
        .map((l) => ({ link: l, item: scheduleItems.find((i) => i.id === l.predecessor_item_id) })),
    [links, editItem, scheduleItems]
  );

  const successors = useMemo(
    () =>
      links
        .filter((l) => l.predecessor_item_id === editItem?.id)
        .map((l) => ({ link: l, item: scheduleItems.find((i) => i.id === l.successor_item_id) })),
    [links, editItem, scheduleItems]
  );

  const isFinalComplete = (item: ScheduleItem): boolean =>
    item.is_complete === true ||
    item.progress >= 100 ||
    (item.actual_end_date != null && item.actual_end_date !== '');

  const filterOptions = (items: ScheduleItem[]) => {
    const query = linkSearch.trim().toLowerCase();
    return items.filter((i) => {
      const selectedForEdit = editingLinkId !== null && i.id === linkForm.otherItemId;
      if (!showCompletedOptions && !selectedForEdit && isFinalComplete(i)) return false;
      if (!query) return true;
      return `${i.task_no ?? ''} ${i.title}`.toLowerCase().includes(query);
    });
  };

  const linkedPredIds = useMemo(
    () => new Set(predecessors.map((p) => p.link.predecessor_item_id)),
    [predecessors]
  );

  const linkedSuccIds = useMemo(
    () => new Set(successors.map((s) => s.link.successor_item_id)),
    [successors]
  );

  const availableForPred = useMemo(
    () =>
      filterOptions(
        scheduleItems.filter((i) => {
          if (i.id === editItem?.id) return false;
          if (editingLinkId && i.id === linkForm.otherItemId) return true;
          return !linkedPredIds.has(i.id);
        })
      ),
    [scheduleItems, editItem, linkedPredIds, linkSearch, showCompletedOptions, editingLinkId, linkForm.otherItemId]
  );

  const availableForSucc = useMemo(
    () =>
      filterOptions(
        scheduleItems.filter((i) => {
          if (i.id === editItem?.id) return false;
          if (editingLinkId && i.id === linkForm.otherItemId) return true;
          return !linkedSuccIds.has(i.id);
        })
      ),
    [scheduleItems, editItem, linkedSuccIds, linkSearch, showCompletedOptions, editingLinkId, linkForm.otherItemId]
  );

  const idToLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of scheduleItems) {
      map.set(item.id, item.title || `Task ${item.task_no ?? ''}`.trim());
    }
    return map;
  }, [scheduleItems]);

  if (!editItem) return null;

  const resetForm = () => {
    setLinkForm({ otherItemId: '', link_type: 'FS', lag_days: 0 });
    setLinkSearch('');
    setShowCompletedOptions(false);
    setLocalError('');
    setLinkSaveSuccess('');
    setEditingLinkId(null);
  };

  const validate = (otherId: string, isPredecessor: boolean): string | null => {
    const predId = isPredecessor ? otherId : editItem.id;
    const succId = isPredecessor ? editItem.id : otherId;
    const result = validateScheduleLinkMutation({
      predecessorId: predId,
      successorId: succId,
      links,
      editingLinkId,
      currentItemId: editItem.id,
      idToLabel,
    });
    if (result.ok === true) return null;
    return result.error;
  };

  const handleSavePred = async () => {
    const isEditing = editingLinkId !== null;
    const err = validate(linkForm.otherItemId, true);
    if (err) {
      setLocalError(err);
      setLinkSaveSuccess('');
      return;
    }
    try {
      await onUpsertLink(editingLinkId, {
        predecessor_item_id: linkForm.otherItemId,
        successor_item_id: editItem.id,
        link_type: linkForm.link_type,
        lag_days: Number(linkForm.lag_days) || 0,
      });
      setAddingPred(false);
      resetForm();
      setLocalError('');
      setLinkSaveSuccess(isEditing ? 'Link updated. Downstream dates recalculated automatically.' : 'Link added. Downstream dates recalculated automatically.');
      setTimeout(() => setLinkSaveSuccess(''), 6000);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Failed to save link. Please try again.');
    }
  };

  const handleSaveSucc = async () => {
    const isEditing = editingLinkId !== null;
    const err = validate(linkForm.otherItemId, false);
    if (err) {
      setLocalError(err);
      setLinkSaveSuccess('');
      return;
    }
    try {
      await onUpsertLink(editingLinkId, {
        predecessor_item_id: editItem.id,
        successor_item_id: linkForm.otherItemId,
        link_type: linkForm.link_type,
        lag_days: Number(linkForm.lag_days) || 0,
      });
      setAddingSucc(false);
      resetForm();
      setLocalError('');
      setLinkSaveSuccess(isEditing ? 'Link updated. Downstream dates recalculated automatically.' : 'Link added. Downstream dates recalculated automatically.');
      setTimeout(() => setLinkSaveSuccess(''), 6000);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Failed to save link. Please try again.');
    }
  };

  const handleDelete = async (linkId: string) => {
    if (!window.confirm('Remove this link?')) return;
    await onRemoveLink(linkId);
  };

  const scrollToLinkForm = () => {
    requestAnimationFrame(() => {
      editFormAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const startEdit = (link: ScheduleLink, isPred: boolean) => {
    setLocalError('');
    setLinkSaveSuccess('');
    setLinkForm({
      otherItemId: isPred ? link.predecessor_item_id : link.successor_item_id,
      link_type: link.link_type,
      lag_days: link.lag_days,
    });
    setEditingLinkId(link.id);
    setLinkSearch('');
    if (isPred) {
      setAddingPred(true);
      setAddingSucc(false);
    } else {
      setAddingSucc(true);
      setAddingPred(false);
    }
    scrollToLinkForm();
  };

  const renderLinkRow = (entry: { link: ScheduleLink; item?: ScheduleItem }, isPred: boolean) => {
    const { link, item } = entry;
    if (!item) return null;
    const active = ACTIVE_TYPES.includes(link.link_type as (typeof ACTIVE_TYPES)[number]);
    const isDeleting = deletingLinkId === link.id;
    const isEditingThis = editingLinkId === link.id;

    if (isEditingThis) {
      return (
        <div key={link.id} className="py-2 border-b border-gray-50 last:border-0">
          <p className="text-xs font-semibold text-[#374151] mb-2">
            Editing link to <span className="text-[#1A1A1A]">{item.title}</span>
          </p>
          {renderAddForm(
            isPred,
            isPred ? handleSavePred : handleSaveSucc,
            () => {
              if (isPred) setAddingPred(false);
              else setAddingSucc(false);
              resetForm();
            },
            isPred ? availableForPred : availableForSucc,
            editFormAnchorRef
          )}
        </div>
      );
    }

    return (
      <div
        key={link.id}
        className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-2 border-b border-gray-50 last:border-0"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#1A1A1A] truncate">{item.title}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-[10px] text-[#9CA3AF] bg-gray-100 px-1.5 py-0.5 rounded">
              Task {item.task_no ?? '—'}
            </span>
            <span className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-1.5 py-0.5 rounded">
              {link.link_type}
              {!active && (
                <span className="ml-1 text-[8px] font-normal text-[#9CA3AF]">(read-only)</span>
              )}
            </span>
            <span className="text-[10px] text-[#6B7280]">Lag {link.lag_days}d</span>
          </div>
        </div>
        {canEdit && active && (
          <div className="flex items-center gap-1 sm:flex-shrink-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                startEdit(link, isPred);
              }}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 cursor-pointer"
              title="Edit"
            >
              <i className="ri-pencil-line text-[#374151] text-xs"></i>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(link.id);
              }}
              disabled={isDeleting}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#FEF2F2] hover:bg-[#FEE2E2] cursor-pointer disabled:opacity-50"
              title="Delete"
            >
              {isDeleting ? (
                <i className="ri-loader-4-line animate-spin text-[#EF4444] text-xs"></i>
              ) : (
                <i className="ri-delete-bin-line text-[#EF4444] text-xs"></i>
              )}
            </button>
          </div>
        )}
        {canEdit && !active && (
          <div className="flex items-center gap-1 sm:flex-shrink-0">
            <span className="text-[10px] text-[#9CA3AF] bg-gray-50 px-2 py-1 rounded">Deprecated</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(link.id);
              }}
              disabled={isDeleting}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#FEF2F2] hover:bg-[#FEE2E2] cursor-pointer disabled:opacity-50"
              title="Remove"
            >
              {isDeleting ? (
                <i className="ri-loader-4-line animate-spin text-[#EF4444] text-xs"></i>
              ) : (
                <i className="ri-delete-bin-line text-[#EF4444] text-xs"></i>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderAddForm = (
    isPred: boolean,
    onSave: () => Promise<void>,
    onCancel: () => void,
    available: ScheduleItem[],
    anchorRef?: RefObject<HTMLDivElement | null>
  ) => (
    <div ref={anchorRef} className="bg-[#F9FAFB] rounded-xl p-3 mt-2 space-y-3">
      <div>
        <label className="block text-[#555555] text-xs font-semibold mb-1.5">
          {isPred ? 'Select predecessor' : 'Select successor'}
        </label>
        <input
          type="text"
          value={linkSearch}
          onChange={(e) => setLinkSearch(e.target.value)}
          placeholder="Search task name..."
          className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] mb-2"
        />
        <label className="flex items-center gap-2 text-xs text-[#6B7280] mb-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showCompletedOptions}
            onChange={(e) => setShowCompletedOptions(e.target.checked)}
            className="w-3.5 h-3.5 accent-[#E8621A]"
          />
          Show completed tasks
        </label>
        <select
          value={linkForm.otherItemId}
          onChange={(e) => setLinkForm((p) => ({ ...p, otherItemId: e.target.value }))}
          className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A]"
        >
          <option value="">Choose an item...</option>
          {available.map((i) => (
            <option key={i.id} value={i.id}>
              {i.task_no ?? '-'} — {i.title}
            </option>
          ))}
        </select>
        {available.length === 0 && (
          <p className="text-[11px] text-[#9CA3AF] mt-1.5">No unfinished tasks match your search.</p>
        )}
      </div>
      <div>
        <label className="block text-[#555555] text-xs font-semibold mb-1.5">Link type</label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {ALL_TYPES.map((t) => {
            const active = ACTIVE_TYPES.includes(t as (typeof ACTIVE_TYPES)[number]);
            const isSelected = linkForm.link_type === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => {
                  if (active) setLinkForm((p) => ({ ...p, link_type: t }));
                }}
                className={`flex flex-col items-start gap-0.5 px-3 py-2 rounded-xl border-2 text-left transition-colors ${
                  isSelected ? 'border-[#E8621A] bg-[#FEF0E7]' : active ? 'border-gray-100 hover:border-gray-300' : 'border-gray-100 opacity-50'
                } ${active ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-[#1A1A1A]">{t}</span>
                  {!active && (
                    <span className="text-[8px] font-bold text-[#9CA3AF] bg-gray-100 px-1 rounded">Deprecated</span>
                  )}
                </div>
                <span className="text-[9px] text-[#6B7280]">{LINK_LABELS[t].desc}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="block text-[#555555] text-xs font-semibold mb-1.5">Lag days</label>
        <input
          type="number"
          min={-365}
          max={365}
          value={linkForm.lag_days}
          onChange={(e) => setLinkForm((p) => ({ ...p, lag_days: Number(e.target.value) }))}
          className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A]"
        />
        {linkForm.lag_days < 0 && (
          <p className="text-[#F59E0B] text-xs mt-1">
            Negative lag is allowed but working-day negative lag is not fully supported yet.
          </p>
        )}
      </div>
      {localError && <p className="text-[#EF4444] text-xs bg-[#FEF2F2] rounded-xl px-3 py-2">{localError}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[#374151] text-xs font-bold cursor-pointer whitespace-nowrap"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!linkForm.otherItemId || savingLink}
          className="flex-1 py-2.5 rounded-xl bg-[#E8621A] text-white text-xs font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-1"
        >
          {savingLink ? <i className="ri-loader-4-line animate-spin"></i> : null}
          {editingLinkId ? 'Update Link' : 'Save Link'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-1">
      {/* Link save success toast */}
      {linkSaveSuccess && (
        <div className="flex items-start gap-2 bg-[#F0FDF4] rounded-xl p-3 border border-[#BBF7D0] mb-3">
          <i className="ri-check-line text-[#059669] text-base flex-shrink-0 mt-0.5"></i>
          <p className="text-[#059669] text-sm font-medium">{linkSaveSuccess}</p>
          <button onClick={clearLinkSuccess} className="ml-auto w-5 h-5 flex items-center justify-center rounded cursor-pointer hover:bg-white/60">
            <i className="ri-close-line text-[#059669] text-xs"></i>
          </button>
        </div>
      )}
      <p className="text-[#6B7280] text-xs mb-3">
        Connect this schedule item to other stages so schedule changes can move linked items automatically.
      </p>

      {/* Predecessors */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-[#374151]">Predecessors</span>
          {canEdit && (
            <button
              type="button"
              onClick={() => { setAddingPred(true); setAddingSucc(false); resetForm(); scrollToLinkForm(); }}
              className="text-xs font-semibold text-[#E8621A] hover:text-[#B84C10] cursor-pointer whitespace-nowrap"
            >
              + Add Predecessor
            </button>
          )}
        </div>
        {predecessors.length === 0 && !addingPred && <p className="text-xs text-[#9CA3AF] italic">No predecessors linked.</p>}
        {predecessors.map((p) => renderLinkRow(p, true))}
        {addingPred && !editingLinkId && renderAddForm(true, handleSavePred, () => { setAddingPred(false); resetForm(); }, availableForPred, editFormAnchorRef)}
      </div>

      {/* Successors */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-[#374151]">Successors</span>
          {canEdit && (
            <button
              type="button"
              onClick={() => { setAddingSucc(true); setAddingPred(false); resetForm(); scrollToLinkForm(); }}
              className="text-xs font-semibold text-[#E8621A] hover:text-[#B84C10] cursor-pointer whitespace-nowrap"
            >
              + Add Successor
            </button>
          )}
        </div>
        {successors.length === 0 && !addingSucc && <p className="text-xs text-[#9CA3AF] italic">No successors linked.</p>}
        {successors.map((s) => renderLinkRow(s, false))}
        {addingSucc && !editingLinkId && renderAddForm(false, handleSaveSucc, () => { setAddingSucc(false); resetForm(); }, availableForSucc, editFormAnchorRef)}
      </div>

      {/* Helper text */}
      <div className="mt-3 bg-[#F9FAFB] rounded-xl p-3 text-[11px] text-[#6B7280] leading-relaxed space-y-1.5">
        <p className="font-medium text-[#374151] mb-1">How links work</p>
        <div>
          <p><span className="font-semibold text-[#1A1A1A]">FS</span> — {LINK_LABELS.FS.desc}</p>
        </div>
        <div>
          <p><span className="font-semibold text-[#1A1A1A]">SS</span> — {LINK_LABELS.SS.desc}</p>
        </div>
        <p><span className="font-semibold text-[#1A1A1A]">Lag</span> — working days to wait before the linked item starts.</p>
      </div>

      {/* Manual recalc trigger — as fallback */}
      <div className="mt-2">
        <button
          onClick={async () => { await onRecalcLinkedItems(); }}
          disabled={savingLink}
          className="text-xs text-[#9CA3AF] hover:text-[#E8621A] cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1 disabled:opacity-50"
        >
          <i className="ri-refresh-line text-xs"></i>
          Manually recalculate all schedule dates
        </button>
      </div>
    </div>
  );
}
